import { MoreHorizontal } from 'lucide-react'
import { useEffect, useId, useRef, type ReactNode } from 'react'

import { cx } from './cx'

/*
 | Menú de acciones secundarias, sobre el `popover` nativo.
 |
 | Mismo criterio que en `Dialog`: se usa lo del navegador en vez de replicarlo.
 | El atributo `popover` trae gratis el cierre al pulsar fuera y con Escape, y la
 | capa superior —así que no compite con ningún `z-index` ni lo recorta el
 | `overflow` de un contenedor, que es lo que rompe la mayoría de los menús
 | hechos a mano dentro de una tarjeta.
 |
 | Lo que NO trae es la posición: la capa superior lo saca del flujo, así que se
 | coloca a mano desde el rectángulo del botón. Se hace con JS y no con anclaje
 | CSS porque el anclaje todavía no está en Firefox, y un menú que aparece en
 | otra esquina no es un detalle estético.
 |
 | No es un `role="menu"`: eso obliga a navegación con flechas y a que los hijos
 | sean `menuitem`. Para tres acciones, un grupo de botones con tabulador normal
 | es más correcto y no promete un teclado que no existe.
 */

export function Menu({ label, children }: { label: string; children: ReactNode }) {
  const id = useId()
  const trigger = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)

  /*
   | Se coloca al ABRIRSE, no al pulsar el botón.
   |
   | El manejador del clic corre antes de que el navegador muestre el popover, y
   | mientras está oculto mide 0×0: calcular ahí la posición lo mandaba fuera de
   | la pantalla por la derecha. El evento `toggle` llega ya renderizado.
   */
  useEffect(() => {
    const menu = panel.current
    if (menu === null) return

    const colocar = (event: Event): void => {
      if ((event as ToggleEvent).newState !== 'open') return

      const boton = trigger.current
      if (boton === null) return

      const rect = boton.getBoundingClientRect()
      const { offsetWidth: ancho, offsetHeight: alto } = menu

      // Alineado a la derecha del botón. Si no cabe debajo —móvil, botón cerca
      // del borde inferior— se abre hacia arriba.
      const cabeAbajo = rect.bottom + alto + 8 < window.innerHeight

      menu.style.left = `${Math.max(8, Math.min(rect.right - ancho, window.innerWidth - ancho - 8))}px`
      menu.style.top = cabeAbajo ? `${rect.bottom + 6}px` : `${Math.max(8, rect.top - alto - 6)}px`
    }

    menu.addEventListener('toggle', colocar)

    return () => menu.removeEventListener('toggle', colocar)
  }, [])

  return (
    <>
      <button
        ref={trigger}
        type="button"
        popoverTarget={id}
        aria-label={label}
        className={cx(
          'grid h-9 w-9 place-items-center rounded-control text-ink-subtle transition-colors',
          'hover:bg-surface-sunken hover:text-ink',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent',
        )}
      >
        <MoreHorizontal size={18} strokeWidth={1.75} aria-hidden />
      </button>

      <div
        ref={panel}
        id={id}
        popover="auto"
        className="m-0 w-56 rounded-card border border-line bg-surface-raised p-1 shadow-float"
      >
        <div className="flex flex-col">{children}</div>
      </div>
    </>
  )
}

export function MenuItem({
  children,
  onClick,
  tone = 'neutral',
}: {
  children: ReactNode
  onClick: () => void
  tone?: 'neutral' | 'danger'
}) {
  return (
    <button
      type="button"
      // Se cierra buscando el popover ancestro: `popovertargetaction` exigiría
      // que cada acción conociera el id del menú que la contiene.
      onClick={(event) => {
        event.currentTarget.closest<HTMLElement>('[popover]')?.hidePopover()
        onClick()
      }}
      className={cx(
        'flex items-center gap-2.5 rounded-control px-3 py-2.5 text-left text-label transition-colors',
        tone === 'danger' ? 'text-danger hover:bg-danger-subtle' : 'text-ink hover:bg-surface-sunken',
      )}
    >
      {children}
    </button>
  )
}
